import { get, some } from 'lodash';

/**
 * Create HashMap for applications by account id
 *
 * @export
 * @param {Array<Object>} accountRoles
 * @param {Object} application
 * @returns HashMap by account id
 */
export function getAppsByAccount(accountRoles, application) {
  let appsByAccount = accountRoles.reduce((acc, curr) => {
    const accountRole = acc[curr.account_id];

    // Return immediately if data is already available or role is not developer.
    // Developer role has extra info. So we take that info with additional details
    // in the application object (secret, server tokens)
    if (accountRole && curr.role !== 'developer') {
      return {
        ...acc,
        [curr.account_id]: {
          ...accountRole,
          roles: [...accountRole.roles, curr.role],
        },
      };
    }

    const account_id = get(curr, 'account_id');
    const applications = get(curr, `account.applications.slice`, []);
    const role = get(curr, 'role');

    if (!account_id || applications.length === 0) {
      return acc;
    }

    const display_name = get(curr, `account.display_name`, null);
    const roles = [...get(accountRole, 'roles', []), role];

    acc = {
      ...acc,
      [account_id]: {
        account_id,
        display_name,
        roles,
        applications,
      },
    };

    return acc;
  }, {});

  if (application) {
    const { id, account_id } = application;

    if (!appsByAccount[account_id]) {
      appsByAccount = addMissingAccount(appsByAccount, application);
    }

    if (
      appsByAccount[account_id] &&
      !some(appsByAccount[account_id].applications, app => app.id === id)
    ) {
      appsByAccount = addMissingApplication(appsByAccount, application);
    }
  }

  return appsByAccount;
}

/**
 * Adds user's root account to accounts HashMap
 * Sometimes a user's root account is not included in the account roles
 *
 * @export
 * @param {Array<Object>} accountRoles
 * @param {Object} application
 * @returns HashMap by account id with user's root account
 */
function addMissingAccount(appsByAccount, application) {
  const { account_id } = application;

  return {
    ...appsByAccount,
    [account_id]: {
      account_id,
      display_name: null,
      roles: [],
      applications: [{ ...application }],
    },
  };
}

/**
 * Adds user's root application to accounts HashMap
 * Sometimes a user's root application is not included in the account roles' applications
 *
 * @export
 * @param {Array<Object>} accountRoles
 * @param {Object} application
 * @returns HashMap by account id with user's root application
 */
function addMissingApplication(appsByAccount, application) {
  const { account_id } = application;

  return {
    ...appsByAccount,
    [account_id]: {
      ...appsByAccount[account_id],
      applications: [
        ...appsByAccount[account_id].application,
        { ...application },
      ],
    },
  };
}
